import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Panel, Button, Banner, EmptyState, Field, Modal, ConfirmDialog, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, del, patch } from '../../lib/api'
import { money, dateShort } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { useCheckout, type CheckoutStart } from '../../lib/checkout'

/**
 * Billing under Handpicked's Business Terms v1.3: the monthly tab (Article 5),
 * the week under way and its lock, weekly invoices line by line (6.1, 6.2), and
 * anything that has stopped new leads (6.4, 6.5, 13.6).
 */
export type WeeklyInfo = {
  tab: {
    amount_cents: number; started_at: string | null; skip_period: string | null; credit_cents: number; skip_min_cents: number; can_skip: boolean; discount_bps: number
    next: { period: string; date: string; due_now: boolean; skipped: boolean } | null
  }
  locks_at: string
  this_week: { unclaimed: number; claimed: number; claimed_cents: number }
  pauses: { id: string; kind: 'payment_failed' | 'no_card' | 'chargeback' | 'cause' | 'self'; reason: string; started_at: string; statement_id: string | null; chargeback_id: string | null }[]
  failed_tab: { period: string; amount_cents: number } | null
  weekly_cap_cents: number | null; week_spend_cents: number
  open_offer: { id: string; level: string } | null
}
export type WeeklyBill = {
  id: string; period: string; status: 'invoiced' | 'paid'; invoiced_cents: number; invoiced_leads: number; invoiced_at: string; paid_at: string | null
  receipt_url: string | null; week_start: string | null; leads_cents: number | null; tab_credit_cents: number | null; tab_cash_cents: number | null
  trial?: boolean
}
type Line = {
  id: string; summary: string; tier_name: string | null; job_tier: string | null; claimed_at: string; claim_source: string | null
  full_price_cents: number; list_cents: number; prepaid_credit_cents: number; prepaid_cash_cents: number; overage_cents: number; sent_at: string
}

const fmt = (d: string | null, o: Intl.DateTimeFormatOptions) => (d ? new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', ...o }).format(new Date(d)) : '')
const weekLabel = (start: string | null) => {
  if (!start) return ''
  const a = new Date(`${start}T12:00:00Z`)
  const b = new Date(a.getTime() + 6 * 86400000)
  const o: Intl.DateTimeFormatOptions = { timeZone: 'UTC', month: 'short', day: 'numeric' }
  return `${a.toLocaleDateString('en-US', o)} to ${b.toLocaleDateString('en-US', { ...o, year: 'numeric' })}`
}
const monthOf = (p: string) => new Date(`${p}-15T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' })

function PayButton({ label, start }: { label: string; start: () => Promise<CheckoutStart> }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const checkout = useCheckout(label)
  const go = async () => {
    setBusy(true)
    try { checkout.open(await start(), () => setBusy(false)) } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return <><Button onClick={go} disabled={busy}>{busy ? 'Opening' : label}</Button>{checkout.element}</>
}

/** An open chargeback as the portal gets it (lib/chargebacks.js withMoney, plus whether paying it restarts leads). */
export type OpenChargeback = {
  id: string; amount_cents: number; withdrawn_cents: number; fee_cents: number; repaid_cents: number; owed_cents: number
  pending_return: boolean; decided_for_company: boolean; paused: boolean; restarts_leads: boolean
}

/** What has stopped new leads, and the one thing that starts them again. Chargebacks have their own banners. */
export function PauseBanners({ info, bills, canPay, chargebacks = [] }: { info: WeeklyInfo; bills: WeeklyBill[]; canPay: boolean; chargebacks?: OpenChargeback[] }) {
  const shown = info.pauses.filter((p) => p.kind !== 'self' && !(p.kind === 'chargeback' && chargebacks.some((c) => c.id === p.chargeback_id)))
  if (!shown.length) return null
  return (
    <div className="stack-tight" style={{ marginBottom: 'var(--s-6)' }}>
      {shown.map((p) => {
        const bill = p.statement_id ? bills.find((b) => b.id === p.statement_id && b.status === 'invoiced') : null
        const action = p.kind === 'payment_failed' && bill && canPay
          ? <PayButton label={`Pay ${money(bill.invoiced_cents)}`} start={() => post<CheckoutStart>(`/api/client/statements/${bill.period}/pay`, {})} />
          : p.kind === 'payment_failed' && info.failed_tab && canPay
            ? <PayButton label={`Pay ${money(info.failed_tab.amount_cents)}`} start={() => post<CheckoutStart>('/api/client/deposits', { amount_cents: info.failed_tab!.amount_cents })} />
            : null
        return (
          <Banner key={p.id} tone="neg" title="New leads are paused" action={action}>
            {p.reason}{' '}
            {p.kind === 'no_card' ? 'Add a card below and leads start again straight away.'
              : p.kind === 'payment_failed' ? 'Paying it starts leads again straight away. If your bank asks you to confirm the payment, you can do it on the payment page.'
                : p.kind === 'chargeback' ? 'Leads start again once the disputed charge is settled.'
                  : 'Leads start again once this is fixed.'}
            {' '}Leads already sent to you are not affected.
          </Banner>
        )
      })}
    </div>
  )
}

/**
 * Each open chargeback: what is owed and why (Business Terms 6.5), with a Pay
 * button when there is something to pay. Shown whatever the billing cycle.
 */
export function ChargebackBanners({ chargebacks, canPay, orgName }: { chargebacks: OpenChargeback[]; canPay: boolean; orgName: string }) {
  if (!chargebacks.length) return null
  return (
    <div className="stack-tight" style={{ marginBottom: 'var(--s-6)' }}>
      {chargebacks.map((cb) => (
        <Banner key={cb.id} tone="neg" title="A disputed charge"
          action={cb.owed_cents >= 50 && canPay ? <PayButton label={`Pay ${money(cb.owed_cents)}`} start={() => post<CheckoutStart>(`/api/client/chargebacks/${cb.id}/pay`, {})} /> : null}>
          <ChargebackWords cb={cb} canPay={canPay} orgName={orgName} />{cb.paused ? ' New leads are paused until it is settled; leads already sent to you are not affected.' : ''}
        </Banner>
      ))}
    </div>
  )
}

function ChargebackWords({ cb, canPay, orgName }: { cb: OpenChargeback; canPay: boolean; orgName: string }) {
  const pay = !canPay ? `Contact ${orgName} to repay it.`
    : !cb.paused ? 'Please repay it.'
      : cb.restarts_leads ? 'Paying it starts leads again straight away.'
        : 'Leads start again once everything shown here is settled.'
  if (cb.pending_return) {
    return <>Your card issuer decided the dispute over the {money(cb.amount_cents)} charge in {orgName}'s favor, and Stripe is returning the money. There is nothing to pay right now.</>
  }
  if (cb.owed_cents <= 0) {
    return <>A charge of {money(cb.amount_cents)} was disputed with your card issuer. Nothing has been taken back yet, so there is nothing to pay. If the dispute was a mistake, ask your bank to withdraw it.</>
  }
  if (cb.decided_for_company) {
    return <>Your card issuer decided the dispute over the {money(cb.amount_cents)} charge in {orgName}'s favor, but Stripe kept its {money(cb.fee_cents)} dispute fee. Under the Business Terms (6.5) that fee is yours to repay{cb.repaid_cents ? `: ${money(cb.owed_cents)} is left` : ''}. {pay}</>
  }
  const parts = [cb.withdrawn_cents ? `the ${money(cb.withdrawn_cents)} charge` : null, cb.fee_cents ? `Stripe's ${money(cb.fee_cents)} dispute fee` : null].filter(Boolean)
  return <>A charge of {money(cb.amount_cents)} was disputed with your card issuer. Under the Business Terms (6.5) {parts.length ? parts.join(' and ') : 'it'} {parts.length > 1 ? 'stay' : 'stays'} owed: {money(cb.owed_cents)}{cb.repaid_cents ? ` after the ${money(cb.repaid_cents)} you have repaid` : ''}. {pay}</>
}

/** The tab, and the week under way. */
export function TabAndWeek({ info, onChanged, inTrial = false }: { info: WeeklyInfo; onChanged: () => void; inTrial?: boolean }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const t = info.tab
  const skip = async (on: boolean) => {
    setBusy(true)
    try {
      if (on) { const r = await post<{ period: string }>('/api/client/tab/skip', {}); toast(`${monthOf(r.period)} will be skipped`, 'pos') }
      else { await del('/api/client/tab/skip'); toast('Your tab will be charged as usual', 'pos') }
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const skipping = t.next && t.skip_period === t.next.period
  return (
    <div className="two-col" style={{ marginBottom: 'var(--s-6)' }}>
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Your monthly tab</h2><span className="muted">{money(t.amount_cents)} a month</span></div>
        <div className="cost-rows">
          <div className="cost-row"><span>Tab credit now</span><span className="num">{money(t.credit_cents)}</span></div>
          <div className="cost-row"><span className="muted">Leads paid from it cost</span><span className="num muted">{Math.round(t.discount_bps / 100)}% less</span></div>
          {t.next && <div className="cost-row"><span className="muted">Next charge</span><span className="num muted">{skipping ? `Skipped (${monthOf(t.next.period)})` : `${dateShort(`${t.next.date}T12:00:00`)}, ${money(t.amount_cents)}`}</span></div>}
        </div>
        {!t.started_at ? <p className="muted" style={{ marginBottom: 0 }}>Your first tab is charged when your account is approved, then once a month on that day.</p> : (
          <div className="spread wrap-gap" style={{ marginTop: 'var(--s-5)', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13 }}>Unused credit rolls over. With {money(t.skip_min_cents)} or more you can skip a month.</span>
            {skipping
              ? <Button variant="secondary" disabled={busy} onClick={() => skip(false)}>Undo skip</Button>
              : <Button variant="secondary" disabled={busy || !t.can_skip} onClick={() => skip(true)} title={t.can_skip ? undefined : `Needs ${money(t.skip_min_cents)} of credit`}>Skip next month</Button>}
          </div>
        )}
      </Panel>
      <Panel>
        <div className="panel-header"><h2 className="panel-title">This week</h2><span className="muted">locks {fmt(info.locks_at, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span></div>
        <div className="cost-rows">
          <div className="cost-row"><span>Claimed</span><span className="num">{info.this_week.claimed} · {money(info.this_week.claimed_cents)}</span></div>
          <div className="cost-row"><span>Not claimed yet</span><span className="num">{info.this_week.unclaimed}</span></div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          {inTrial ? 'In your trial, nothing is charged at the end of the week: at 11:59 PM Sunday, Arizona time, each lead from the week is claimed at the tier it reached, and they are all invoiced together when your trial ends.'
            : 'At 11:59 PM Sunday, Arizona time, every lead from the week is claimed at the tier it reached and invoiced: your tab credit pays first, at the tab price, and the rest is charged to your card at the full price.'}
          {info.this_week.unclaimed > 0 && <> <Link to="/portal/leads">See leads to claim</Link>.</>}
        </p>
      </Panel>
    </div>
  )
}

/** One weekly invoice: totals, then every lead on it when opened. */
function InvoiceRow({ bill, canPay }: { bill: WeeklyBill; canPay: boolean }) {
  const [open, setOpen] = useState(false)
  const lines = useApi<{ lines: Line[] }>(open ? `/api/client/statements/${bill.id}/lines` : null)
  return (
    <div className="bill-row-wrap">
      <div className="bill-row">
        <div>
          <strong>{bill.trial ? 'Your trial' : bill.week_start ? weekLabel(bill.week_start) : bill.period}</strong>
          <div className="muted" style={{ fontSize: 13 }}>
            {bill.invoiced_leads} lead{bill.invoiced_leads === 1 ? '' : 's'}
            {bill.tab_cash_cents ? ` · ${money(bill.tab_cash_cents)} from tab` : ''} · <button type="button" className="link-button" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? 'Hide leads' : 'See leads'}</button>
          </div>
        </div>
        <strong className="num">{money(bill.invoiced_cents)}</strong>
        <div style={{ textAlign: 'right' }}>
          {bill.status === 'paid'
            ? <span className="row-tight" style={{ justifyContent: 'flex-end', alignItems: 'center' }}><span className="pill is-good">{bill.invoiced_cents ? `Paid ${dateShort(bill.paid_at)}` : 'Paid from tab'}</span>{bill.receipt_url && <a href={bill.receipt_url} target="_blank" rel="noopener noreferrer" title="Stripe receipt"><Icon of={ExternalLink} size={15} /></a>}</span>
            : canPay ? <PayButton label={`Pay ${money(bill.invoiced_cents)}`} start={() => post<CheckoutStart>(`/api/client/statements/${bill.period}/pay`, {})} /> : <span className="pill is-warn">Due</span>}
        </div>
      </div>
      {open && (
        <div className="invoice-lines">
          {!lines.data ? <span className="muted">Loading</span> : lines.data.lines.map((l) => (
            <div key={l.id} className="cost-row">
              <span><Link to={`/portal/leads/${l.id}`}>{l.summary}</Link> <span className="muted">{[l.tier_name, l.job_tier, l.claim_source === 'lock' ? 'claimed at the lock' : null].filter(Boolean).join(' · ')}</span></span>
              <span className="num">
                {l.prepaid_credit_cents > 0 && <span className="muted">{money(l.prepaid_cash_cents)} from tab{l.overage_cents > 0 ? ' + ' : ''}</span>}
                {l.overage_cents > 0 && <>{money(l.overage_cents)}</>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function WeeklyInvoices({ bills, canPay }: { bills: WeeklyBill[]; canPay: boolean }) {
  const weeks = bills.filter((b) => /W/.test(b.period))
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Weekly invoices</h2><span className="muted">made after the Sunday lock</span></div>
      {weeks.length === 0 ? <EmptyState title="No invoices yet" body="Each week's claimed leads are invoiced just after 11:59 PM Sunday, Arizona time." /> : (
        <div className="bill-list">{weeks.map((b) => <InvoiceRow key={b.id} bill={b} canPay={canPay} />)}</div>
      )}
    </Panel>
  )
}

/**
 * New leads on or off (Ezra's package; terms 13.7): the pause button, with the
 * pause rule's prompts, and the weekly budget. Leads already delivered still
 * lock and bill either way.
 */
export function LeadsSwitch({ info, onChanged }: { info: WeeklyInfo; onChanged: () => void }) {
  const toast = useToast()
  const mine = info.pauses.find((p) => p.kind === 'self')
  const [busy, setBusy] = useState(false)
  const [final, setFinal] = useState<string | null>(null)
  const [offer, setOffer] = useState<{ id: string; text: string } | null>(null)
  const [cap, setCap] = useState(info.weekly_cap_cents ? String(info.weekly_cap_cents / 100) : '')
  const pause = async (confirmFinal = false) => {
    setBusy(true)
    try {
      const r = await post<{ paused?: boolean; needs_confirm?: boolean; notice?: string; offer?: { id: string; level: string; text: string | null } | null }>('/api/client/pause', { confirm_final: confirmFinal })
      if (r.needs_confirm) { setFinal(r.notice || ''); return }
      setFinal(null)
      if (r.offer?.text && ['offer', 'again'].includes(r.offer.level)) setOffer({ id: r.offer.id, text: r.offer.text })
      toast('New leads are paused. Leads already sent still lock and bill as usual.', 'pos')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const resume = async () => {
    setBusy(true)
    try { await post('/api/client/resume', {}); toast('New leads are on again', 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const answer = async (a: 'declined' | 'interested') => {
    if (!offer) return
    try { await post(`/api/client/pause-offers/${offer.id}`, { answer: a }); toast(a === 'interested' ? 'Thank you. We will be in touch.' : 'Understood.', 'pos') } catch (e) { toast((e as Error).message, 'neg') }
    setOffer(null)
  }
  const saveCap = async () => {
    const cents = cap.trim() ? Math.round(Number(cap) * 100) : null
    try { await patch('/api/client/weekly-cap', { cents }); toast(cents ? `Weekly budget set to ${money(cents)}` : 'No weekly budget', 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">New leads</h2>
        <span className={`pill ${mine ? 'is-warn' : 'is-good'}`}>{mine ? 'Paused by you' : info.pauses.length ? 'Paused' : 'On'}</span>
      </div>
      <div className="spread wrap-gap" style={{ alignItems: 'center' }}>
        <span className="muted">{mine ? 'You paused new leads. Start them again any time.' : 'Pause new leads any time, for example when you are fully booked. Leads already sent still lock and bill.'}</span>
        {mine ? <Button onClick={resume} disabled={busy}>Start new leads</Button> : <Button variant="secondary" onClick={() => pause()} disabled={busy}>Pause new leads</Button>}
      </div>
      <div className="form-grid" style={{ marginTop: 'var(--s-5)', alignItems: 'end' }}>
        <Field label="Weekly budget, dollars (optional)" id="wk-cap" inputMode="decimal" placeholder="No limit" value={cap} onChange={(e) => setCap(e.target.value)} />
        <div><Button variant="secondary" onClick={saveCap}>Save budget</Button></div>
      </div>
      <p className="muted" style={{ marginBottom: 0, fontSize: 13 }}>
        {info.weekly_cap_cents ? `${money(info.week_spend_cents)} of ${money(info.weekly_cap_cents)} so far this week. ` : ''}
        Once this week's leads reach your budget, new leads stop until Monday. Tiers can still rise before the Sunday lock, so the bill can land a little over it.
      </p>
      {final !== null && <ConfirmDialog title="Before you pause" tone="neg" confirmLabel="Pause anyway" body={final} onConfirm={() => pause(true)} onCancel={() => setFinal(null)} />}
      {offer && (
        <Modal title="Can we help?" onClose={() => setOffer(null)}
          footer={<><Button variant="secondary" onClick={() => answer('declined')}>No thanks</Button><Button onClick={() => answer('interested')}>Tell me more</Button></>}>
          <p style={{ margin: 0 }}>{offer.text}</p>
        </Modal>
      )}
    </Panel>
  )
}
